import {
    createContext,
    useContext,
    useReducer,
    type ReactNode
} from 'react';

// Menu item type
export type MenuItem = {
    name: string;
    url: string;
    iconName?: string; // Optional now
    children?: MenuItem[]; // Submenus
};

type MenuState = {
    items: MenuItem[];
};

type MenuContextType = MenuState & {
    addMenu: (item: MenuItem) => void;
};

// Initial menu
const initialMenu: MenuItem[] = [

    { name: 'Get Started', url: '/docs/get-started', iconName: 'play' },
    {
        name: 'Layout',
        url: '/docs/layout',
        iconName: 'layout',
        children: [
            { name: 'Structure', url: '/docs/layout' },
            { name: 'Container', url: '/docs/layout/container' },
            { name: 'Stack', url: '/docs/layout/stack' },
            { name: 'Grid', url: '/docs/layout/grid' },
        ]
    },
    {
        name: 'Content',
        url: '/docs/content',
        iconName: 'layout',
        children: [
            { name: 'Typography', url: '/docs/content/typography' },
            { name: 'Icon', url: '/docs/content/icons' },
            { name: 'Image & Figures', url: '/docs/content/image' },
            { name: 'Data table', url: '/docs/content/data-table' },
            { name: 'Table', url: '/docs/content/table' },
        ]
    },
    {
        name: 'Forms',
        url: '/docs/forms',
        iconName: 'form',
        children: [
            { name: 'Overview', url: '/docs/forms', },
            { name: 'Form Control', url: '/docs/forms/form-control' },
            { name: 'Select', url: '/docs/forms/select' },
            { name: 'Checkbox & radios', url: '/docs/forms/checks-radios' },
            { name: 'Range', url: '/docs/forms/range' },
            { name: 'Input group', url: '/docs/forms/input-group' },
            { name: 'Validation', url: '/docs/forms/validation' },
            { name: 'Html Editor', url: '/docs/forms/html-editor' },
        ]
    },
    {
        name: 'Components',
        url: '/docs/components',
        iconName: 'stack',
        children: [
            { name: 'Accordion', url: '/docs/components/accordion' },
            { name: 'Alert', url: '/docs/components/alert' },
            { name: 'Badge', url: '/docs/components/badge' },
            { name: 'Breadcrumb', url: '/docs/components/breadcrumb' },
            { name: 'Button', url: '/docs/components/button' },
            { name: 'Button group', url: '/docs/components/buttongroup' },
            { name: 'Card', url: '/docs/components/card' },
            { name: 'Dropdown', url: '/docs/components/dropdown' },
            { name: 'List group', url: '/docs/components/listgroup' },
            { name: 'Modal', url: '/docs/components/modal' },
            { name: 'Navbar', url: '/docs/components/navbar' },
            { name: 'Navs & tabs', url: '/docs/components/nav' },
            { name: 'Offcanvas', url: '/docs/components/offcanvas' },
            { name: 'Pagination', url: '/docs/components/pagination' },
            /* { name: 'Placeholder', url: '/component/placeholder' },*/
            { name: 'Popover', url: '/docs/components/popover' },
            { name: 'Progress', url: '/docs/components/progress' },
            { name: 'Spinner', url: '/docs/components/spinner' },
            /*  { name: 'Scrollspy', url: '/component/scrollspy' },*/
            { name: 'Toast', url: '/docs/components/toast' },
            { name: 'Tooltip', url: '/docs/components/tooltip' }
        ]
    }
];

const initialState: MenuState = {
    items: initialMenu
};

// Actions
type MenuAction = { type: 'ADD_MENU'; payload: MenuItem };

// Reducer
const menuReducer = (state: MenuState, action: MenuAction): MenuState => {
    switch (action.type) {
        case 'ADD_MENU':
            return { items: [...state.items, action.payload] };
        default:
            return state;
    }
};

// Context
const MenuContext = createContext<MenuContextType | null>(null);

export const useMenuContext = () => {
    const context = useContext(MenuContext);
    if (!context) {
        throw new Error('MenuContext is null');
    }
    return context;
};

// Provider
type MenuContextProviderProps = {
    children: ReactNode;
};

export const MenuContextProvider = ({ children }: MenuContextProviderProps) => {
    const [state, dispatch] = useReducer(menuReducer, initialState);

    const value: MenuContextType = {
        items: state.items,
        addMenu: (item) => dispatch({ type: 'ADD_MENU', payload: item })
    };

    return (
        <MenuContext.Provider value={value}>
            {children}
        </MenuContext.Provider>
    );
};
